import React from 'react';
import Slider from '@mui/material/Slider';
import { useField } from 'formik';
import { Box, Grid, InputLabel, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import type { GridProps, SliderOwnProps, SliderProps } from '@mui/material';

type SparkSliderProps = {
  name: string;
  label: string;
  min?: number;
  max: number;
  step?: number;
  size?: GridProps['xs'];
  gridProps?: GridProps;
  helpText?: string;
  onChange?: (value: number | number[]) => void;
  renderPercentage?: boolean;
  subHeaderText?: string;
  renderValue?: boolean;
  valueLabelDisplay?: SliderOwnProps['valueLabelDisplay'];
  decimalPrecision?: number;
};

const SparkSlider = ({
  name,
  label,
  min = 0,
  max,
  step = 1,
  size = 12,
  helpText,
  onChange,
  subHeaderText = '',
  renderPercentage = false,
  gridProps,
  renderValue = true,
  valueLabelDisplay = 'auto',
  decimalPrecision,
}: SparkSliderProps) => {
  const [field, , helper] = useField<number>(name);
  const [sliderValue, setSliderValue] = React.useState<number>(field.value);

  const _onChange: SliderProps['onChange'] = (_, value) => {
    if (decimalPrecision) {
      const preciseValue = parseFloat(
        (value as number).toFixed(decimalPrecision)
      );
      setSliderValue(preciseValue as number);
      onChange && onChange(preciseValue);
      return;
    }
    setSliderValue(value as number);
    onChange && onChange(value as number);
  };

  const formatSlider = (value: number) =>
    renderPercentage ? `${value}%` : `${value}`;

  return (
    <Grid item xs={size} direction='column' padding={1.75} {...gridProps}>
      <InputLabel
        sx={{
          display: 'flex',
          gap: 0.5,
          fontSize: 16,
          justifyContent: 'space-between',
        }}
      >
        <Box component='span' display='flex' alignItems='center' gap={0.5}>
          {label}
          {helpText && (
            <Tooltip title={helpText} placement='top' arrow>
              <InfoOutlinedIcon fontSize='small' />
            </Tooltip>
          )}
        </Box>
        {renderValue && (
          <Typography>
            {field.value}
            {renderPercentage && '%'}
          </Typography>
        )}
      </InputLabel>
      {subHeaderText && (
        <Typography color='#A3A3A3' fontSize={12} textAlign='left'>
          {subHeaderText}
        </Typography>
      )}
      <Slider
        {...field}
        value={sliderValue}
        onChange={_onChange}
        onChangeCommitted={(_, value) => {
          helper.setValue(value as number);
        }}
        min={min}
        max={max}
        step={step}
        valueLabelDisplay={valueLabelDisplay}
        valueLabelFormat={formatSlider}
      />
    </Grid>
  );
};

export default SparkSlider;
